import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig, preloadImage, onAnimationEnd } from '@theme/utilities';

const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

class ProductCardCta extends Component {
  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  #preloadImage = () => {
    const button = this.querySelector('[data-product-variant-media]');
    const image = button?.dataset?.productVariantMedia;
    if (image) preloadImage(image);
  };

  /**
   * Opens the shade drawer for a product with variants.
   * @param {Event} event - The click event.
   */
  async openShadeDrawer(event) {
    event.preventDefault();

    const button = event.target.closest('.select-shade__button');
    if (!button) return;

    const productUrl = button.dataset.productUrl;
    const productId = button.dataset.productId;
    if (!productUrl) return;

    await customElements.whenDefined('shade-drawer-component');
    const shadeDrawer = document.querySelector('shade-drawer-component');
    if (shadeDrawer) {
      shadeDrawer.open(this.#resolveShadeDrawerUrl(productUrl), productId);
    }
  }

  /**
   * Adds `?variant=` so the drawer opens on the shade picked on the card.
   * Prefers the checked swatch — set synchronously on click — over the card link,
   * which only catches up once the picker's section fetch lands.
   *
   * @param {string} productUrl - The base product URL rendered on the button.
   * @returns {string} The URL to load.
   */
  #resolveShadeDrawerUrl(productUrl) {
    const card = this.closest('product-card');
    if (!card) return productUrl;

    const swatch = card.querySelector(
      'swatches-variant-picker-component input[type="radio"][data-variant-id]:checked'
    );

    if (swatch instanceof HTMLInputElement && swatch.dataset.variantId) {
      // Combined listings point each swatch at its own child product.
      const url = new URL(swatch.dataset.connectedProductUrl || productUrl, window.location.href);
      url.searchParams.set('variant', swatch.dataset.variantId);
      return url.toString();
    }

    const cardLink = card.querySelector('a[ref="productCardLink"]');
    if (cardLink instanceof HTMLAnchorElement && cardLink.href && new URL(cardLink.href).searchParams.has('variant')) {
      return cardLink.href;
    }

    return productUrl;
  }

  /**
   * Adds a single-variant product directly to cart.
   * @param {Event} event - The click event.
   */
  addToBag(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const button = this.querySelector('.select-shade__button');
    if (button) button.disabled = true;

    // Start animations immediately for instant feedback
    this.#animateAddToCart();
    // Skip the fly-to-cart animation on mobile — the cart icon is already
    // close to the CTA and the animation can overshoot the viewport.
    if (window.innerWidth >= 768) {
      this.#animateFlyToCart();
    }

    const formData = new FormData(form);

    const cartItems = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItems.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const config = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          this.dispatchEvent(new CartErrorEvent(this.id || '', data.message));
          if (button) button.disabled = false;
          return;
        }

        this.dispatchEvent(
          new CartAddEvent({}, formData.get('id')?.toString() || '', {
            source: 'product-card-cta',
            itemCount: Number(formData.get('quantity')) || 1,
            sections: data.sections,
          })
        );

        // If this CTA was triggered from inside the hamburger menu drawer,
        // fully close the menu (synchronously, bypassing the slide-out
        // animation) so its <details> open attribute and html[scroll-lock]
        // are cleared before the cart drawer takes over.
        const headerDrawerDetails = this.closest('header-drawer')?.querySelector('details[open]');
        if (headerDrawerDetails instanceof HTMLDetailsElement) {
          headerDrawerDetails.classList.remove('menu-open');
          headerDrawerDetails.removeAttribute('open');
          headerDrawerDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }

        // The header renders two cart-drawer-component instances — one inside
        // .desktop-navigation and one inside .mobile-navigation — and only the
        // viewport-matching parent is visible. Pick the one in the visible
        // navigation so the dialog actually shows on mobile
        const isMobile = window.innerWidth < 768;
        const cartDrawer = document.querySelector(
          isMobile
            ? '.mobile-navigation cart-drawer-component'
            : '.desktop-navigation cart-drawer-component'
        ) || document.querySelector('cart-drawer-component');
        if (cartDrawer) cartDrawer.open();

        if (button) button.disabled = false;
      })
      .catch((error) => {
        console.error('Add to cart error:', error);
        if (button) button.disabled = false;
      });
  }

  /**
   * Animates the button text from "Add to Bag" to "Added".
   */
  #animateAddToCart() {
    const button = this.querySelector('.select-shade__button');
    if (!button) return;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!button.classList.contains('atc-added')) {
      button.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        button.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Creates a fly-to-cart element that animates the product image to the cart icon.
   */
  #animateFlyToCart() {
    const button = this.querySelector('[data-product-variant-media]');
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = button?.dataset?.productVariantMedia;

    if (!cartIcon || !button || !image) return;

    const flyToCartElement = /** @type {HTMLElement} */ (document.createElement('fly-to-cart'));
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = button;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }
}

if (!customElements.get('product-card-cta')) {
  customElements.define('product-card-cta', ProductCardCta);
}
